import { AHIcon, AHText, Box } from "arkh";
import React from "react";
import { graphql, useFragment } from "react-relay";
import { useIntlFormattedMonthDay } from "../../../Team/TeamHomeNavigator/TeamScheduleListItem/useIntlFormattedMonthDay";
import { FormattedMessage } from "react-intl";
import { WorkspaceRoutinesListItemScheduleMonthly_schedule$key } from "./__generated__/WorkspaceRoutinesListItemScheduleMonthly_schedule.graphql";
import { WorkspaceRoutinesListItemScheduleWeekly_schedule$key } from "./__generated__/WorkspaceRoutinesListItemScheduleWeekly_schedule.graphql";
import { useWeekdays } from "../../../../common/useWeekdays";
import { capitalizeFirstLetter } from "../../../../utils";
import { WorkspaceRoutinesListItemSchedule_schedule$key } from "./__generated__/WorkspaceRoutinesListItemSchedule_schedule.graphql";
import { WorkspaceRoutinesListItemScheduleDaily_schedule$key } from "./__generated__/WorkspaceRoutinesListItemScheduleDaily_schedule.graphql";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { useWeekdayFormattedString } from "@/src/useWeekdayFormattedString";

function WorkspaceRoutinesListItemScheduleContainer(props: {
  title: React.ReactNode;
  start: React.ReactNode;
  end: React.ReactNode;
}) {
  return (
    <>
      <AHText type="subtitle" color="darkText">
        {props.title}
      </AHText>
      <Box
        style={{ flexDirection: "row", alignItems: "center" }}
        spaceTop="xxsmall"
      >
        <AHText>{props.start}</AHText>
        <AHIcon
          type="arrow-right"
          spaceHorizontal="xsmall"
          color="washedText"
          size="xsmall"
        />
        <AHText>{props.end}</AHText>
      </Box>
    </>
  );
}

function WorkspaceRoutinesListItemScheduleDaily(props: {
  schedule: WorkspaceRoutinesListItemScheduleDaily_schedule$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemScheduleDaily_schedule on RoutineScheduleDaily {
        weekdays {
          weekday
          startTime {
            hour
            minute
          }
        }
      }
    `,
    props.schedule
  );

  const intl = useIntlTokens();

  const str = useWeekdayFormattedString(
    schedule.weekdays.map((weekday) => weekday.weekday)
  );

  const time = schedule.weekdays[0].startTime!;
  return (
    <>
      <AHText type="subtitle" color="darkText">
        {intl.Daily}
      </AHText>
      <Box
        style={{ flexDirection: "row", alignItems: "center" }}
        spaceTop="xxsmall"
      >
        <AHText>
          {("00" + time.hour).slice(-2)}:{("00" + time.minute).slice(-2)} {str}
        </AHText>
      </Box>
    </>
  );
}

function WorkspaceRoutinesListItemScheduleWeekly(props: {
  schedule: WorkspaceRoutinesListItemScheduleWeekly_schedule$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemScheduleWeekly_schedule on RoutineScheduleWeekly {
        startWeekday
        endWeekday
      }
    `,
    props.schedule
  );
  const intl = useIntlTokens();
  const weekday = useWeekdays("short");
  return (
    <WorkspaceRoutinesListItemScheduleContainer
      title={intl.Weekly}
      start={
        schedule.startWeekday !== null
          ? capitalizeFirstLetter(weekday[schedule.startWeekday])
          : null
      }
      end={
        schedule.endWeekday !== null
          ? capitalizeFirstLetter(weekday[schedule.endWeekday])
          : null
      }
    />
  );
}
function WorkspaceRoutinesListItemScheduleMonthly(props: {
  schedule: WorkspaceRoutinesListItemScheduleMonthly_schedule$key;
}) {
  const schedule = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemScheduleMonthly_schedule on RoutineScheduleMonthly {
        startDay
        endDay
      }
    `,
    props.schedule
  );
  const ordinalFormatter = useIntlFormattedMonthDay("short");
  return (
    <WorkspaceRoutinesListItemScheduleContainer
      title={
        <FormattedMessage
          defaultMessage="Monthly"
          description="WorkspaceRoutineListItemScheduleMonthly label"
        />
      }
      start={ordinalFormatter(schedule.startDay)}
      end={schedule.endDay ? ordinalFormatter(schedule.endDay) : null}
    />
  );
}
export function WorkspaceRoutinesListItemSchedule(props: {
  schedule: WorkspaceRoutinesListItemSchedule_schedule$key | null;
}) {
  const schedule = useFragment(
    graphql`
      fragment WorkspaceRoutinesListItemSchedule_schedule on RoutineSchedule {
        __typename
        ... on RoutineScheduleDaily {
          ...WorkspaceRoutinesListItemScheduleDaily_schedule
        }
        ... on RoutineScheduleMonthly {
          ...WorkspaceRoutinesListItemScheduleMonthly_schedule
        }
        ... on RoutineScheduleWeekly {
          ...WorkspaceRoutinesListItemScheduleWeekly_schedule
        }
      }
    `,
    props.schedule
  );
  return (
    <>
      {schedule?.__typename === "RoutineScheduleWeekly" ? (
        <WorkspaceRoutinesListItemScheduleWeekly schedule={schedule} />
      ) : null}
      {schedule?.__typename === "RoutineScheduleMonthly" ? (
        <WorkspaceRoutinesListItemScheduleMonthly schedule={schedule} />
      ) : null}
      {schedule?.__typename === "RoutineScheduleDaily" ? (
        <WorkspaceRoutinesListItemScheduleDaily schedule={schedule} />
      ) : null}
      {schedule === null ? (
        <AHText>
          <FormattedMessage
            defaultMessage="Not scheduled"
            description="WorkspaceRoutineListItemSchedule not scheduled"
          />
        </AHText>
      ) : null}
    </>
  );
}
